import React, {
  CSSProperties, ReactElement, useEffect, useRef,
} from 'react';
import moment from 'moment';
import * as echarts from 'echarts';
import { EChartsOption, EChartsType } from 'echarts';
import { useCurrentWidth } from 'react-socks';
import {
  ECHARTS_LOADING_OPTION,
  LEVELS_OF_SERVICE,
  TITLE_MAP,
  USE_DARK_MODE,
} from '../../../util/constants';
import { ChartType } from '../../../types/ChartType';
import generateGuid from '../../../util/guid';
import { AggDataElement } from '../../../types/AggDataElement';
import { totallyDarkeChartsTheme } from '../../../util/theme';
import { calculateFoldedMetrics } from '../../../util/util';
import useDebouncedEffect from '../../../util/UseDebouncedEffect';

type PPDHistogramProps = {
  histogramType: ChartType;
  // apiUrl: string;
  aggData: AggDataElement[];
  isLoading: boolean;
} & typeof defaultProps;

const defaultProps = {
  style: {} as CSSProperties,
};

const PPDHistogram = ({
  histogramType,
  aggData,
  style,
  isLoading,
}: PPDHistogramProps): ReactElement => {
  // const [aggData, setTrajData] = useState<AggDataElement[]>([]);
  const id = generateGuid();
  const chart = useRef<EChartsType>();

  const width = useCurrentWidth();

  useEffect(() => {
    const opt = getOption();

    if (!chart.current) {
      echarts.registerTheme('totally-dark-theme', totallyDarkeChartsTheme);
      chart.current = echarts.init(document.getElementById(id) as HTMLElement,
        (USE_DARK_MODE) ? 'totally-dark-theme' : 'light',
        {
          renderer: 'canvas',
          locale: 'EN',
        });
      // chart.current?.on('resize', () => {
      //     if (chart.current) chart.current?.resize()
      // });
      // @ts-ignore
      chart.current?.setOption(opt, { notMerge: true });
    }
    // TODO switch these for production
    // chart.current?.setOption({series: opt.series});
    chart.current?.setOption(opt, { notMerge: true });
    chart.current?.resize();
  }, [histogramType, aggData]);

  useEffect(() => {
    if (chart.current) {
      if (isLoading) {
        chart.current?.showLoading('default', ECHARTS_LOADING_OPTION);
      } else {
        chart.current?.hideLoading();
      }
    }
  }, [isLoading]);

  useDebouncedEffect(() => chart.current && chart.current?.resize(), [width], 50);

  const getOption = (): EChartsOption => {
    const daysOfMonth = Array.from(Array(31)
      .keys())
      .map((d) => String(d + 1));
    const foldOn = (d: AggDataElement) => String(moment.unix(d.timestamp)
      .date());
    const foldedMetrics = calculateFoldedMetrics(aggData, foldOn, daysOfMonth, histogramType);

    const seriesData = foldedMetrics.map((f) => [daysOfMonth.indexOf(f.category), f.metric]);

    // decimal (0-1) to percentage (0-100)
    if (histogramType !== 'los') {
      seriesData.forEach((d) => {
        // eslint-disable-next-line no-param-reassign
        d[1] *= 100;
      });
    }

    return {
      grid: {
        top: '40',
        bottom: '40',
        left: '50',
        right: '50',
      },
      title: {
        text: TITLE_MAP[histogramType],
        left: 'center',
      },
      xAxis: {
        type: 'category',
        data: Array.from(Array(31)
          .keys()),
        splitLine: { show: false },
        name: 'Day of Month',
        nameGap: 25,
        nameLocation: 'middle',
        nameTextStyle: {
          fontSize: 14,
          color: USE_DARK_MODE ? '#fff' : '#000',
        },
        axisLabel: {
          color: USE_DARK_MODE ? '#fff' : '#000',
        },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 100,
        name: histogramType === 'los' ? 'Average Control Delay' : 'Percentage of Vehicles',
        nameGap: 30,
        nameLocation: 'middle',
        nameTextStyle: {
          fontSize: 14,
          color: USE_DARK_MODE ? '#fff' : '#000',
        },
        axisLabel: {
          color: USE_DARK_MODE ? '#fff' : '#000',
        },
      },
      // tooltip: {},
      series: {
        type: 'bar',
        data: seriesData,
        emphasis: {
          focus: 'series',
          itemStyle: {
            shadowBlur: 10,
            shadowColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
        animation: true,
        animationDelay: (idx) => idx * 50,
        ...(histogramType === 'los' && {
          markLine: {
            silent: true,
            symbol: ['none', 'none'],
            data: Object.keys(LEVELS_OF_SERVICE)
              .map((los) => ({
                yAxis: LEVELS_OF_SERVICE[los].min || 0,
                lineStyle: {
                  color: LEVELS_OF_SERVICE[los].color,
                },
                label: {
                  formatter: los,
                },
              })),
          },
        }
        ),
      },
      ...(histogramType === 'los' && {
        visualMap: {
          type: 'piecewise',
          // pieces: visualMapPieces
          pieces: Object.keys(LEVELS_OF_SERVICE)
            .map((l) => ({
              ...LEVELS_OF_SERVICE[l],
              label: l,
            })),
          show: false,
          // inRange: Object.keys(LEVELS_OF_SERVICE).map(k => LEVELS_OF_SERVICE[k].color)
        },
      }),
      ...(histogramType !== 'los' && {
        visualMap: {
          type: 'continuous',
          min: 0,
          max: 100,
          inRange: {
            ...(['sf', 'dsb'].includes(histogramType)
              && { color: ['#CCFFCC', '#FFFF00', '#FF0000', '#EA00EA', '#EA00EA', '#EA00EA', '#EA00EA', '#EA00EA', '#EA00EA'] }),
            ...(histogramType === 'aog'
              && { color: ['#FF0000', '#FFFF00', '#CCFFCC'] }),
          },
          show: false,
        },
      }),
    };
  };

  return (
    <div style={style} id={id} />
  );
  // <div>
  // {/*<div style={{position: 'absolute', height: '90%', marginLeft: `${50}px`}}>*/}
  // {/*    <div>*/}
  // {/*        <svg id={`svg-histogram-${histogramType}`}*/}
  // {/*             className={`svg svg-histogram svg-histogram-${histogramType}`}></svg>*/}
  // {/*    </div>*/}
  // {/*</div>*/}
  // </div>
};
export default PPDHistogram;
